<template>
    <v-card flat class="pa-0">
        <v-card-text class="pa-0">
            <!-- Indicador de carga -->
            <div v-if="loading" class="text-center pa-8">
                <v-progress-circular
                    indeterminate
                    color="#1F1C91"
                    size="64"
                ></v-progress-circular>
                <p class="mt-4 grey--text">Cargando tickets...</p>
            </div>
            
            <div v-else class="kanban-container">
                <div
                    v-for="estado in estadosFiltrados"
                    :key="estado.id"
                    class="kanban-column"
                >
                    <div
                        class="kanban-column-header"
                        :style="{ backgroundColor: getColorEstado(estado.id) }"
                    >
                        <h3 class="white--text text-uppercase">
                            {{ estado.descripcion }}
                        </h3>
                        <v-chip small dark class="ml-2">
                            {{ getTicketsByEstado(estado.id).length }}
                        </v-chip>
                    </div>
                    <div
                        class="kanban-column-content"
                        @drop="onDrop($event, estado.id)"
                        @dragover.prevent
                        @dragenter.prevent
                    >
                        <div
                            v-for="ticket in getTicketsByEstado(estado.id)"
                            :key="ticket.id"
                            class="kanban-card"
                            draggable="true"
                            @dragstart="onDragStart($event, ticket)"
                            @click="openTicket(ticket.id)"
                        >
                            <div class="kanban-card-header">
                                <v-chip
                                    x-small
                                    :color="getColorUrgencia(ticket.id_urgencia)"
                                    class="mr-2"
                                >
                                    {{ ticket.urgencia || 'Sin urgencia' }}
                                </v-chip>
                                <v-icon
                                    v-if="ticket.tiene_comentarios_no_leidos"
                                    small
                                    color="red"
                                    class="mr-1"
                                    title="Tienes comentarios nuevos sin leer"
                                >
                                    mdi-help-circle
                                </v-icon>
                                <span class="ticket-id grey--text text--darken-1"
                                    >#{{ ticket.id }}</span
                                >
                            </div>

                            <h4 class="kanban-card-title mt-2">
                                {{ ticket.titulo }}
                            </h4>

                            <div class="kanban-card-info mt-2">
                                <v-icon small class="mr-1" color="grey"
                                    >mdi-account-circle</v-icon
                                >
                                <span class="text-caption grey--text">{{
                                    ticket.cliente || 'Sin cliente'
                                }}</span>
                            </div>

                            <div
                                v-if="ticket.creador && ticket.creador !== 'Sin información'"
                                class="kanban-card-info mt-1"
                            >
                                <v-icon small class="mr-1" color="grey"
                                    >mdi-account-plus</v-icon
                                >
                                <span class="text-caption grey--text">Creado por: {{ ticket.creador }}</span>
                            </div>

                            <div
                                v-if="ticket.proyecto"
                                class="kanban-card-info mt-1"
                            >
                                <v-icon small class="mr-1" color="grey"
                                    >mdi-briefcase</v-icon
                                >
                                <span class="text-caption grey--text">{{
                                    ticket.proyecto
                                }}</span>
                            </div>

                            <div class="kanban-card-footer mt-3">
                                <div class="d-flex align-center">
                                    <v-icon x-small class="mr-1" color="grey"
                                        >mdi-clock-outline</v-icon
                                    >
                                    <span class="text-caption grey--text"
                                        >{{ ticket.tiempo_estimado || 0 }}
                                        min</span
                                    >
                                </div>
                                <v-avatar
                                    size="24"
                                    color="#1F1C91"
                                    class="ml-auto"
                                >
                                    <span class="white--text text-caption">{{
                                        getInitials(
                                            ticket.responsable || 'Sin asignar'
                                        )
                                    }}</span>
                                </v-avatar>
                            </div>

                            <div
                                v-if="ticket.fecha_finalizacion"
                                class="kanban-card-date mt-2"
                            >
                                <v-icon x-small class="mr-1" color="grey"
                                    >mdi-calendar</v-icon
                                >
                                <span class="text-caption grey--text"
                                    >{{ ticket.fecha_finalizacion }}</span
                                >
                            </div>
                        </div>

                        <div v-if="getTicketsByEstado(estado.id).length === 0">
                            <v-card flat class="text-center pa-4 grey--text">
                                <v-icon large color="grey lighten-2"
                                    >mdi-inbox-outline</v-icon
                                >
                                <p class="mt-2 mb-0">No hay tickets</p>
                            </v-card>
                        </div>
                    </div>
                </div>
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
export default {
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        mostrarFinalizados: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            estados: [],
            draggedTicket: null,
        };
    },
    created() {
        this.getEstados();
    },
    methods: {
        getEstados() {
            axios.get('api/get-estado-tickets').then((res) => {
                // Ordenar los estados: Pendiente, En curso, Revisión, Incidencia, Recurrente, Finalizado
                const ordenDeseado = [1, 2, 5, 6, 4, 3]; // IDs en el orden deseado
                this.estados = res.data.success.sort((a, b) => {
                    return ordenDeseado.indexOf(a.id) - ordenDeseado.indexOf(b.id);
                });
            });
        },

        getTicketsByEstado(estadoId) {
            return this.items.filter(
                (ticket) => ticket.id_estado_ticket === estadoId
            );
        },

        getColorEstado(id_estado_ticket) {
            const colors = {
                1: '#FF9800', // Pendiente - warning
                2: '#2196F3', // En curso - info
                3: '#4CAF50', // Finalizado - success
                4: '#9E9E9E', // Recurrente - gray
                5: '#FFEB3B', // Revisión - yellow
            };
            return colors[id_estado_ticket] || '#1F1C91';
        },

        getColorUrgencia(id_urgencia) {
            const colors = {
                1: 'error', // Alta
                2: 'warning', // Media
                3: 'success', // Baja
            };
            return colors[id_urgencia] || 'grey';
        },

        getInitials(name) {
            if (!name) return '?';
            const words = name.split(' ');
            if (words.length >= 2) {
                return (words[0][0] + words[1][0]).toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
        },

        onDragStart(event, ticket) {
            this.draggedTicket = ticket;
            event.dataTransfer.effectAllowed = 'move';
        },

        onDrop(event, newEstadoId) {
            if (!this.draggedTicket) return;

            if (this.draggedTicket.id_estado_ticket !== newEstadoId) {
                const ticketId = this.draggedTicket.id;
                const oldEstadoId = this.draggedTicket.id_estado_ticket;
                
                // Actualizar localmente de inmediato (optimistic update)
                this.draggedTicket.id_estado_ticket = newEstadoId;
                
                // Emitir actualización inmediata al padre con el ticket modificado
                this.$emit('ticket-estado-cambiado', {
                    ticketId: ticketId,
                    nuevoEstado: newEstadoId,
                    antiguoEstado: oldEstadoId
                });

                // Actualizar en el servidor
                axios
                    .put(
                        `api/tickets/${ticketId}/cambiar-estado`,
                        {
                            id_estado_ticket: newEstadoId,
                        }
                    )
                    .then((res) => {
                        // Todo OK
                    })
                    .catch((err) => {
                        console.error('Error al actualizar estado:', err);
                        // Revertir el cambio si falla
                        this.$emit('ticket-estado-cambiado', {
                            ticketId: ticketId,
                            nuevoEstado: oldEstadoId,
                            antiguoEstado: newEstadoId
                        });
                        
                        this.$store.dispatch(
                            'snackbar/showSnackbar',
                            'Error al actualizar el estado del ticket'
                        );
                    });
            }

            this.draggedTicket = null;
        },

        openTicket(ticketId) {
            // Emitir evento para que el padre maneje la apertura del ticket
            this.$emit('open-ticket', ticketId);
        },
    },
    computed: {
        estadosFiltrados() {
            // Si no se muestran finalizados, filtrar el estado 3 (finalizado)
            if (!this.mostrarFinalizados) {
                return this.estados.filter((estado) => estado.id !== 3);
            }
            return this.estados;
        },
    },
};
</script>

<style scoped>
.kanban-container {
    display: flex;
    gap: 16px;
    overflow-x: auto;
    padding: 16px 0;
    min-height: 500px;
}

.kanban-column {
    flex: 1;
    min-width: 300px;
    max-width: 350px;
    background-color: #f5f5f5;
    border-radius: 8px;
    overflow: hidden;
}

.kanban-column-header {
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.kanban-column-header h3 {
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    letter-spacing: 0.5px;
}

.kanban-column-content {
    padding: 12px;
    max-height: 600px;
    overflow-y: auto;
    min-height: 450px;
}

.kanban-card {
    background: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    cursor: move;
    transition: all 0.3s ease;
    border: 2px solid transparent;
}

.kanban-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
    border-color: #1f1c91;
}

.kanban-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.ticket-id {
    font-size: 12px;
    font-weight: 500;
}

.kanban-card-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.kanban-card-info {
    display: flex;
    align-items: center;
    font-size: 12px;
}

.kanban-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
}

.kanban-card-date {
    display: flex;
    align-items: center;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
}

/* Scrollbar personalizada */
.kanban-column-content::-webkit-scrollbar {
    width: 6px;
}

.kanban-column-content::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.kanban-column-content::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
}

.kanban-column-content::-webkit-scrollbar-thumb:hover {
    background: #555;
}

.kanban-container::-webkit-scrollbar {
    height: 8px;
}

.kanban-container::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.kanban-container::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
}

.kanban-container::-webkit-scrollbar-thumb:hover {
    background: #555;
}
</style>

